/**
 * Acting on a comment the team can see (backend #128, #125): edit and delete
 * (the author only; a delete can be undone for ten minutes), Mark done and
 * reopen (anyone, on a thread's first comment), and M7's draft on a thread
 * (accept, dismiss, undo). Every one is optimistic on the post's threads and
 * rolls back only the comment or draft it touched.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import { reconcilePlan } from "../api/calendarCache";
import { conceptVersionsKey } from "../api/useConceptVersions";
import { slotBriefKey } from "../api/useProductionBrief";
import {
  patchComment,
  patchDraft,
  postThreadsKey,
  restoreComment,
  restoreDraft,
  type ApiComment,
  type ApiCommentDraft,
  type CommentSnapshot,
  type CommentSurface,
} from "./commentApi";

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) return (err.response?.data as { message?: string })?.message ?? fallback;
  return fallback;
}

type CommentOp =
  | { op: "edit"; surface: CommentSurface; commentId: string; body: string }
  | { op: "delete" | "restore" | "resolve" | "reopen"; surface: CommentSurface; commentId: string };

const OP_FAILED: Record<CommentOp["op"], string> = {
  edit: "Your edit was not saved",
  delete: "The comment was not deleted",
  restore: "The comment could not come back",
  resolve: "Not marked done",
  reopen: "Not reopened",
};

export function useCommentActions({ slotId, planId }: { slotId: string | null; planId: string }) {
  const qc = useQueryClient();
  const key = postThreadsKey(slotId);
  const mutationKey = ["commentOp", slotId];

  const settle = () => {
    // Several acts can be out at once; refetch only when the last one lands.
    if (qc.isMutating({ mutationKey }) > 1) return;
    void qc.invalidateQueries({ queryKey: key });
  };

  const op = useMutation({
    mutationKey,
    mutationFn: async (vars: CommentOp) => {
      const base = `/content-calendar/comments/${vars.surface}/${vars.commentId}`;
      if (vars.op === "edit") return (await axiosInstance.patch<{ comment: ApiComment }>(base, { body: vars.body })).data;
      if (vars.op === "delete") return (await axiosInstance.delete<{ comment: ApiComment }>(base)).data;
      return (await axiosInstance.post<{ comment: ApiComment }>(`${base}/${vars.op}`)).data;
    },
    onMutate: async (vars): Promise<CommentSnapshot> => {
      if (!slotId) return null;
      await qc.cancelQueries({ queryKey: key });
      const now = new Date().toISOString();
      return patchComment(qc, slotId, vars.commentId, (comment) => {
        switch (vars.op) {
          case "edit":
            return { ...comment, body: vars.body, editedAt: now };
          case "delete":
            return { ...comment, deleted: true };
          case "restore":
            return { ...comment, deleted: false };
          case "resolve":
            return { ...comment, resolvedAt: comment.resolvedAt ?? now };
          case "reopen":
            return { ...comment, resolvedAt: null, resolvedByName: null };
        }
      });
    },
    onError: (err, vars, snapshot) => {
      if (slotId) restoreComment(qc, slotId, snapshot ?? null);
      queue.add({ title: OP_FAILED[vars.op], description: errorMessage(err, "Please try again."), variant: "error" });
    },
    onSuccess: (_data, vars, snapshot) => {
      if (vars.op !== "delete" || !snapshot) return;
      queue.add({
        title: "Comment deleted",
        variant: "success",
        action: {
          label: "Undo",
          onPress: () => op.mutate({ op: "restore", surface: vars.surface, commentId: vars.commentId }),
        },
      });
    },
    onSettled: settle,
  });

  const draft = useMutation({
    mutationKey,
    mutationFn: async (vars: { draftId: string; decision: "accept" | "dismiss" | "undo" }) =>
      (await axiosInstance.post<{ draft: ApiCommentDraft }>(`/content-calendar/comment-drafts/${vars.draftId}/${vars.decision}`)).data
        .draft,
    onMutate: async (vars) => {
      if (!slotId) return null;
      await qc.cancelQueries({ queryKey: key });
      return patchDraft(qc, slotId, vars.draftId, (current) =>
        vars.decision === "dismiss"
          ? null
          : { ...current, state: vars.decision === "accept" ? "accepted" : "suggested" },
      );
    },
    onError: (err, vars, snapshot) => {
      if (slotId) restoreDraft(qc, slotId, snapshot ?? null);
      const title =
        vars.decision === "accept" ? "Creo's change was not accepted" : vars.decision === "undo" ? "Not undone" : "Not dismissed";
      queue.add({ title, description: errorMessage(err, "Please try again."), variant: "error" });
    },
    onSuccess: (saved, vars) => {
      if (!slotId) return;
      patchDraft(qc, slotId, vars.draftId, () => (saved.state === "dismissed" ? null : saved));
      // Accept and undo write the team's working version of the idea or brief.
      if (vars.decision !== "dismiss") {
        void qc.invalidateQueries({ queryKey: slotBriefKey(slotId) });
        void qc.invalidateQueries({ queryKey: conceptVersionsKey(slotId) });
        reconcilePlan(qc, planId);
      }
    },
    onSettled: settle,
  });

  return {
    edit: (surface: CommentSurface, commentId: string, body: string) => op.mutate({ op: "edit", surface, commentId, body }),
    remove: (surface: CommentSurface, commentId: string) => op.mutate({ op: "delete", surface, commentId }),
    setDone: (surface: CommentSurface, commentId: string, done: boolean) =>
      op.mutate({ op: done ? "resolve" : "reopen", surface, commentId }),
    decideDraft: (draftId: string, decision: "accept" | "dismiss" | "undo") => draft.mutate({ draftId, decision }),
  };
}
